import { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { BRAND } from '../data/mock';

export default function Contact() {
  useEffect(() => window.scrollTo(0, 0), []);
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill required fields', description: 'Name, email and message are required.' });
      return;
    }
    const stored = JSON.parse(localStorage.getItem('actiwa_contacts') || '[]');
    stored.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('actiwa_contacts', JSON.stringify(stored));
    toast({ title: 'Message sent!', description: "We'll reach out to you soon." });
    setForm({ name: '', email: '', company: '', message: '' });
  };

  return (
    <>
      <section className="section-fade pt-16 pb-8">
        <div className="max-w-4xl mx-auto text-center px-5 lg:px-8">
          <p className="chip mb-4">Contact Us</p>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900">Let's talk</h1>
          <p className="mt-5 text-slate-600 text-lg">
            Have a question? We'd love to hear from you. Fill out the form and our team will get back within 24 hours.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[
              { Icon: Mail, title: 'Email', value: `hello@actiwa.io` },
              { Icon: Phone, title: 'Phone', value: `+91 ${BRAND.phone}` },
              { Icon: MapPin, title: 'Website', value: BRAND.domain },
            ].map(({ Icon, title, value }) => (
              <div key={title} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4 card-lift">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center">
                  <Icon size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</p>
                  <p className="mt-0.5 font-semibold text-slate-900">{value}</p>
                </div>
              </div>
            ))}
            <div className="bg-gradient-to-br from-brand-purple to-brand-purple-dark text-white rounded-2xl p-6">
              <h4 className="font-bold text-lg">Prefer WhatsApp?</h4>
              <p className="text-sm text-white/80 mt-1">Chat with our sales team instantly.</p>
              <a href={`https://wa.me/91${BRAND.phone}`} target="_blank" rel="noreferrer">
                <Button className="mt-4 bg-brand-green hover:bg-brand-green-dark text-white rounded-full">Chat on WhatsApp</Button>
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-7 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-800">Full name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-lg" placeholder="John Doe" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-800">Work email *</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-lg" placeholder="you@company.com" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-800">Company</label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 h-11 rounded-lg" placeholder="Acme Inc." />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-800">Message *</label>
              <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Tell us about your use case..." />
            </div>
            <Button type="submit" className="w-full h-12 bg-brand-purple hover:bg-brand-purple-dark text-white rounded-full">
              Send message <Send size={16} className="ml-2" />
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}
